import { useState, useEffect } from 'react';
/**
 * 键盘检测Hook
 * 用于检测虚拟键盘的显示状态和高度
 */
export const useKeyboardDetector = () => {
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    useEffect(() => {
        // 检测是否为移动设备
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (!isMobile) {
            return; // 非移动设备不进行键盘检测
        }
        const handleResize = () => {
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            const windowHeight = window.innerHeight;
            // 计算键盘高度
            const calculatedKeyboardHeight = windowHeight - viewportHeight;
            // 判断键盘是否可见（高度变化超过100px认为是键盘弹出）
            const isKeyboardNowVisible = calculatedKeyboardHeight > 100;
            setIsKeyboardVisible(isKeyboardNowVisible);
            setKeyboardHeight(isKeyboardNowVisible ? calculatedKeyboardHeight : 0);
            // 更新CSS变量
            document.documentElement.style.setProperty('--keyboard-height', `${isKeyboardNowVisible ? calculatedKeyboardHeight : 0}px`);
            document.documentElement.style.setProperty('--is-keyboard-visible', isKeyboardNowVisible ? 'true' : 'false');
            // 更新body的class
            if (isKeyboardNowVisible) {
                document.body.classList.add('keyboard-visible');
            }
            else {
                document.body.classList.remove('keyboard-visible');
            }
        };
        // 初始检测
        handleResize();
        // 使用visualViewport事件监听（如果支持）
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize);
            window.visualViewport.addEventListener('scroll', handleResize);
        }
        else {
            // 回退到window的resize事件
            window.addEventListener('resize', handleResize);
        }
        // 监听输入框的focus和blur事件
        const handleFocusIn = (e) => {
            const target = e.target;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                // 延迟检测，确保键盘已经弹出
                setTimeout(handleResize, 100);
            }
        };
        const handleFocusOut = (e) => {
            const target = e.target;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                // 延迟检测，确保键盘已经收起
                setTimeout(handleResize, 300);
            }
        };
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        // 清理函数
        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize);
                window.visualViewport.removeEventListener('scroll', handleResize);
            }
            else {
                window.removeEventListener('resize', handleResize);
            }
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);
    return { isKeyboardVisible, keyboardHeight };
};
/**
 * 安全区域检测Hook
 * 用于获取设备的安全区域信息
 */
export const useSafeArea = () => {
    const [safeArea, setSafeArea] = useState({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    });
    useEffect(() => {
        const updateSafeArea = () => {
            // 创建一个临时元素来获取CSS变量值
            const tempElement = document.createElement('div');
            tempElement.style.position = 'absolute';
            tempElement.style.top = '-9999px';
            tempElement.style.left = '-9999px';
            document.body.appendChild(tempElement);
            // 获取计算后的样式
            const computedStyle = window.getComputedStyle(tempElement);
            const newSafeArea = {
                top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top')) || 0,
                right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right')) || 0,
                bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom')) || 0,
                left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left')) || 0,
            };
            // 移除临时元素
            document.body.removeChild(tempElement);
            setSafeArea(newSafeArea);
        };
        // 初始更新
        updateSafeArea();
        // 监听窗口大小变化（安全区域可能在旋转屏幕时改变）
        window.addEventListener('resize', updateSafeArea);
        window.addEventListener('orientationchange', updateSafeArea);
        return () => {
            window.removeEventListener('resize', updateSafeArea);
            window.removeEventListener('orientationchange', updateSafeArea);
        };
    }, []);
    return safeArea;
};
